import type { FilterComponentProps } from '@/app/common/filters/core/filterComponentType.ts';
import type { FilterDataSwitch } from '@/app/common/filters/core/filtersTypes.ts';
import { Switch } from '@lmn/design-system';
import styled from 'styled-components';
import { formatLabelCounter } from '@/app/common/filters/types/shared/utils/labelCounter.tsx';
import { BASE_PATH } from '@/app/components/search/utils/path.ts';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Tooltip from '@/app/components/hotel-detail/Tooltip.tsx';
import { Counter } from '@/app/common/filters/types/shared/styled/Counter.styled.ts';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import { Text } from '@/app/common/filters/types/shared/styled/Text.styled.ts';
import { replacePlaceholdersWithBoldNodes } from '@/app/components/search/utils/translationNodes.tsx';
import { Icon } from '@/app/common/icons/Icon';
import { IconOrLegacyAsset } from '@/app/common/icons/IconOrLegacyAsset';
import useTooltipState from '@/app/components/search/hooks/useTooltipState.ts';

const SwitchContainer = styled.div<{ $evo?: boolean }>`
  //  Switchstyles__Wrapper
  label {
    justify-content: space-between;
    width: 100%;
    /* The evo design draws a switch as a 40px list row. */
    min-height: ${({ $evo }) => ($evo ? 40 : 48)}px;

    // Switchstyles__SwitchWrapper

    > div {
      flex-shrink: 0;
    }

    // Switchstyles__LabelText
    > span {
      width: 100%;
      display: inline-flex;
      justify-content: space-between;
      align-items: center;
      margin-right: 8px;
    }
  }
`;

// A flex item like the .tooltip it wraps, so wrapping it moves nothing.
const TooltipHoverArea = styled.span`
  display: flex;
`;

const isSwitchInput = (target: EventTarget) => target instanceof HTMLElement && target.matches('input[role="switch"]');

export const FilterSwitch = ({ filter, isLoading, handleFilterChanged }: FilterComponentProps) => {
  const { useEvoFilters, totalResults } = useFiltersUiData();
  const disabled = filter?.disabled;
  const filterData = filter.data as FilterDataSwitch;
  const isDisabled = disabled || isLoading;
  const { icon, tooltip } = filterData;
  const baseId = useId();
  const switchId = `${baseId}-switch`;
  const descriptionId = `${baseId}-tip`;

  // The DS Switch forwards no aria-* to its <input>, so the description is set on the node itself.
  useEffect(() => {
    if (!tooltip) return undefined;
    const input = document.getElementById(switchId);
    input?.setAttribute('aria-describedby', descriptionId);
    return () => input?.removeAttribute('aria-describedby');
  }, [tooltip, switchId, descriptionId]);

  // The DS Tooltip takes `open` but no onOpenChange, so hover, focus and Escape are all driven from here.
  // useTooltipState closes any other open tooltip; keyboardOpen keeps this one up until blur (1.4.13).
  const { isOpen, open, close, scheduleClose } = useTooltipState();
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const pointerOver = useRef(false);
  const tooltipOpen = isOpen || keyboardOpen;

  const closeKeyboardTooltip = useCallback(() => {
    setKeyboardOpen(false);
    if (!pointerOver.current) close();
  }, [close]);

  // A switch that turns disabled while focused may lose focus without a blur event.
  useEffect(() => {
    if (isDisabled) closeKeyboardTooltip();
  }, [isDisabled, closeKeyboardTooltip]);

  const handleFocus = useCallback(
    (e: React.FocusEvent) => {
      if (!tooltip || !isSwitchInput(e.target) || !e.target.matches(':focus-visible')) return;
      setKeyboardOpen(true);
      open();
    },
    [tooltip, open]
  );
  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (isSwitchInput(e.target)) closeKeyboardTooltip();
    },
    [closeKeyboardTooltip]
  );
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Escape' || !tooltipOpen) return;
      e.stopPropagation();
      setKeyboardOpen(false);
      close();
    },
    [tooltipOpen, close]
  );
  // Radix ignores touch for hover too; a tap toggles the switch through its label.
  const handlePointerEnter = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === 'touch') return;
      pointerOver.current = true;
      open();
    },
    [open]
  );
  const handlePointerLeave = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === 'touch') return;
      pointerOver.current = false;
      scheduleClose();
    },
    [scheduleClose]
  );

  const handleFilterClicked = (selected: boolean) => {
    const updatedFilter = {
      ...filter,
      data: {
        ...filter.data,
        selected,
      },
    };
    handleFilterChanged([updatedFilter]);
  };

  const countOnCta = !!useEvoFilters && totalResults !== undefined;
  const labelCounter = countOnCta ? undefined : formatLabelCounter(filterData.counter);
  const iconElement = icon ? (
    <IconOrLegacyAsset icon={typeof icon === 'string' ? `${BASE_PATH}/img/icons/${icon}.svg` : icon} size={16} />
  ) : null;
  const circleIcon = <Icon category="Semantics" name="icon-circle-info" size={16} color="iconSecondary" alt="" />;
  const label = (
    <>
      <Text>
        {iconElement}
        <span>
          {filterData.labelEmphasis ? replacePlaceholdersWithBoldNodes(filterData.labelEmphasis) : filterData.label}
        </span>
        {tooltip && (
          // The bubble is a React child of this span, so moving onto it counts as still hovering.
          <TooltipHoverArea onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
            <Tooltip label={<div>{tooltip}</div>} open={tooltipOpen} placement="top" maxWidth={200} className="tooltip">
              {circleIcon}
            </Tooltip>
          </TooltipHoverArea>
        )}
      </Text>
      {labelCounter && <Counter isDisabled={isDisabled}>{labelCounter}</Counter>}
    </>
  );

  return (
    <SwitchContainer
      key={filter.name}
      $evo={useEvoFilters}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <Switch
        checked={filterData.selected}
        // @ts-ignore
        label={label}
        id={switchId}
        ltr={true}
        isLoading={false}
        disabled={isDisabled || isLoading}
        onChange={handleFilterClicked}
      />
      {tooltip && (
        <span id={descriptionId} hidden={true}>
          {tooltip}
        </span>
      )}
    </SwitchContainer>
  );
};
